import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./components/ThemeProvider";
import { LanguageProvider, useTranslation } from "./i18n";
import { Landing } from "./pages/Landing";
import { Dashboard } from "./pages/Dashboard";
import { Library } from "./pages/Library";
import { Search } from "./pages/Search";
import { MediaDetail } from "./pages/MediaDetail";
import { Profile } from "./pages/Profile";
import { Achievements } from "./pages/Achievements";
import { Challenges } from "./pages/Challenges";
import { Feed } from "./pages/Feed";
import { PublicProfile } from "./pages/PublicProfile";
import { AdminPanel } from "./pages/AdminPanel";
import { Sidebar } from "./components/Sidebar";
import { MobileNav } from "./components/MobileNav";
import axios from "axios";

const API = '/api';

// Global axios interceptor: attach Bearer token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// AuthCallback: extracts session_id, calls POST /api/auth/session EXACTLY ONCE
function AuthCallback() {
  const calledRef = useRef(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    // Extract session_id from hash or query params
    let sessionId = null;
    if (window.location.hash) {
      sessionId = new URLSearchParams(window.location.hash.substring(1)).get("session_id");
    }
    if (!sessionId) {
      sessionId = new URLSearchParams(window.location.search).get("session_id");
    }
    if (!sessionId) {
      window.location.replace("/");
      return;
    }

    // Single POST — this is the ONLY session exchange call in the entire app
    axios.post(`${API}/auth/session`, { session_id: sessionId })
      .then((res) => {
        const token = res.data?.session_token;
        if (token) localStorage.setItem("session_token", token);
        window.location.replace("/dashboard");
      })
      .catch(() => {
        window.location.replace("/");
      });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">{t.common.authenticating}</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const { t } = useTranslation();
  
  useEffect(() => {
    if (location.state?.user) { setUser(location.state.user); setIsAuthenticated(true); return; }
    axios.get(`${API}/auth/me`)
      .then(response => { setUser(response.data); setIsAuthenticated(true); })
      .catch(() => { localStorage.removeItem("session_token"); setIsAuthenticated(false); });
  }, [location.state]);
  
  if (isAuthenticated === null) return (<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-pulse text-muted-foreground">{t.common.loading}</div></div>);
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return children({ user, setUser });
}

function AppLayout({ children, user }) {
  return (
    <div className="min-h-screen bg-background">
      <Sidebar user={user} />
      <MobileNav user={user} />
      <main className="lg:pl-64 pt-14 lg:pt-0">
        <div className="px-4 py-4 md:px-6 md:py-6 lg:px-8 lg:py-8 pb-8">
          {children}
        </div>
      </main>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) return <AuthCallback />;
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dashboard" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><Dashboard user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><Library user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><Search user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/media/:itemId" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><MediaDetail user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/achievements" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><Achievements user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><Challenges user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/feed" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><Feed user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/profile/:username" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><PublicProfile user={user} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute>{({ user, setUser }) => (<AppLayout user={user}><Profile user={user} setUser={setUser} /></AppLayout>)}</ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute>{({ user }) => (<AppLayout user={user}><AdminPanel user={user} /></AppLayout>)}</ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="mediashelf-theme">
      <LanguageProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
